import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import { moduleConfig } from './config';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../../services/paginatedList';

type CodeName = { id: number; code?: string | null; name?: string | null };
type EquipRef = { id: number; equipmentCode?: string | null; name?: string | null };
type VesselRef = {
  id: number;
  vesselName?: string | null;
  vesselAbrv?: string | null;
  name?: string | null;
  abrv?: string | null;
};

export interface PmsEquipment {
  id: number;
  vesselId: number;
  departmentId: number;
  systemId?: number | null;
  componentId?: number | null;
  subComponentId?: number | null;
  parentEquipmentId?: number | null;
  equipmentCode: string;
  name: string;
  description?: string | null;
  equipmentType?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  serialNumber?: string | null;
  location?: string | null;
  criticalityId: number;
  statusId: number;
  recordStatusId: number;
  verificationStatusId: number;
  isRotatingEquipment?: boolean;
  isSafetyCritical?: boolean;
  isClassRelated?: boolean;
  isCylinderSpecific?: boolean;
  cylinderNumber?: number | null;
  isActive?: boolean;
  notes?: string | null;
  vessel?: VesselRef | null;
  department?: CodeName | null;
  system?: CodeName | null;
  component?: CodeName | null;
  subComponent?: CodeName | null;
  parentEquipment?: EquipRef | null;
  criticality?: CodeName | null;
  status?: CodeName | null;
  recordStatus?: CodeName | null;
  verificationStatus?: CodeName | null;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

export const usePmsEquipments = (
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) => {
  const q = buildListQuery(searchParams, page, limit, options);
  const { data, error, isLoading, mutate } = useSWR<
    PaginatedApiResponse<PmsEquipment> | PmsEquipment[]
  >(`${moduleConfig.module.api.list}${q}`, fetcher, { revalidateOnFocus: true });
  const normalized = normalizePaginatedListResponse<PmsEquipment>(data, page, limit);
  return {
    data: normalized.data,
    clients: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    isLoading,
    isError: error,
    mutate,
  };
};

export const usePmsEquipment = (id?: number) => {
  const { data, error, isLoading, mutate } = useSWR<PmsEquipment>(
    id != null && Number.isFinite(id) && id > 0 ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data, item: data, isLoading, isError: error, mutate };
};
